import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { UserResultCard, type UserResult } from './UserResultCard';

interface UsersTabPanelProps {
  results: UserResult[];
  total: number;
  isSearching: boolean;
  error: string | null;
  hasIntent: boolean;
  searchTerm: string;
  onSelect: (user: UserResult) => void;
  onClearQuery?: () => void;
}

export const UsersTabPanel = ({
  results,
  isSearching,
  error,
  hasIntent,
  searchTerm,
  onSelect,
  onClearQuery,
}: UsersTabPanelProps) => {
  if (!hasIntent) {
    return (
      <Box
        data-testid="users-empty-state"
        sx={{
          bgcolor: 'background.paper',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 7,
          textAlign: 'center',
          boxShadow: 1,
        }}
      >
        <Typography
          component="h2"
          sx={{ fontFamily: 'serif', fontWeight: 700, fontSize: '1.5rem', mb: 0.75 }}
        >
          Find users
        </Typography>
        <Typography sx={{ color: 'text.secondary', maxWidth: 460, mx: 'auto' }}>
          Type a name, ALI ID, or email to find users.
        </Typography>
      </Box>
    );
  }

  if (isSearching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (results.length === 0) {
    return (
      <Box
        data-testid="users-no-results"
        sx={{
          bgcolor: 'background.paper',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 7,
          textAlign: 'center',
          boxShadow: 1,
        }}
      >
        <Typography
          component="h2"
          sx={{ fontFamily: 'serif', fontWeight: 700, fontSize: '1.5rem', mb: 0.75 }}
        >
          No users{searchTerm !== '' ? ` for “${searchTerm}”` : ''}
        </Typography>
        <Typography sx={{ color: 'text.secondary', maxWidth: 460, mx: 'auto', mb: 2.5 }}>
          Check your spelling or try a different name.
        </Typography>
        {onClearQuery && (
          <Button variant="contained" onClick={onClearQuery}>
            Clear search
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      data-testid="users-results-list"
      sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 } }}
    >
      {results.map((u) => (
        <UserResultCard key={u.userId} user={u} onSelect={onSelect} />
      ))}
    </Box>
  );
};
